import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  IconButton,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { Formik, Form, Field, FieldProps, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useMasterData } from '../../../hooks/useMasterData';
import { StudentStatus, studentsApi } from './api';
import { STUDENT_FIELDS } from './constants';

// Local Layout Components
const PageContainer: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Box sx={{ p: 3, ...sx }}>{children}</Box>
);

const PageHeader: React.FC<{ title: string }> = ({ title }) => (
  <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>{title}</Typography>
);

const Section: React.FC<{ title: string; children: React.ReactNode; defaultExpanded?: boolean; sx?: any }> = ({ title, children, defaultExpanded = false, sx }) => (
  <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 2, ...sx }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      {children}
    </AccordionDetails>
  </Accordion>
);

const getFullUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) return url;
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
  return `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
};

const PhotoUpload: React.FC<{
  label: string;
  value: string;
  onChange: (url: string) => void;
  error?: string | boolean;
}> = ({ label, value, onChange, error }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setUploading(true);
      try {
        const response = await studentsApi.uploadFile(file);
        onChange(response.url);
      } catch (err) {
        alert('Photo upload failed');
      } finally {
        setUploading(false);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" color="textSecondary">{label}</Typography>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={getFullUrl(value)}
          sx={{ width: 100, height: 100, border: error ? '2px solid red' : '1px solid #ccc' }}
        >
          {!value && <CameraAltIcon />}
        </Avatar>
        {uploading && (
          <CircularProgress
            size={24}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px',
            }}
          />
        )}
      </Box>
      <Button
        variant="outlined"
        component="label"
        size="small"
        startIcon={<CloudUploadIcon />}
        disabled={uploading}
      >
        Upload
        <input type="file" hidden accept="image/*" onChange={handleFileChange} />
      </Button>
      {error && (
        <Typography variant="caption" color="error">
          {error as string}
        </Typography>
      )}
    </Box>
  );
};

interface StudentFormProps {
  initialValues?: any;
  onSubmit: (values: any) => Promise<void>;
  isEdit?: boolean;
}

const FormObserver: React.FC<{ isSubmitting: boolean; isValid: boolean; errors: any }> = ({ isSubmitting, isValid, errors }) => {
  useEffect(() => {
    if (isSubmitting && !isValid && Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      const element = document.getElementsByName(firstErrorKey)[0] || document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [isSubmitting, isValid, errors]);
  return null;
};

const StudentForm: React.FC<StudentFormProps> = ({ initialValues, onSubmit, isEdit }) => {
  const navigate = useNavigate();
  const { masters, loading: loadingMasters, fetchMasters, fetchSections: fetchSectionsApi } = useMasterData();
  const { classes, castes, categories, houses, religions, bloodGroups, examBoards, academicSessions, parentServiceTypes, familyRelations, documents: masterDocuments, nationalities, motherTongues, studentTypes, mediums, leavingReasons } = masters;
  const [sections, setSections] = useState<any[]>([]);

  const validationSchema = React.useMemo(() => Yup.object({
    firstName: Yup.string().required('First Name is required'),
    middleName: Yup.string().nullable(),
    lastName: Yup.string().required('Last Name is required'),
    admissionNumber: Yup.string().required('Admission Number is required'),
    scholarNumber: Yup.string().required('Scholar Number is required'),
    email: Yup.string().email('Invalid email').nullable(),
    phone: Yup.string().matches(/^\d{10}$/, { message: 'Mobile must be exactly 10 digits numeric', excludeEmptyString: true }).nullable(),
    classId: Yup.string().required('Class is required'),
    sectionId: Yup.string().required('Section is required'),
    academicSessionId: Yup.string().required('Academic Session is required'),
    aadharNumber: Yup.string().matches(/^\d{12}$/, { message: 'Invalid Aadhar Number (must be 12 digits)', excludeEmptyString: true }).nullable(),
    admissionDate: Yup.date().nullable(),
    dateOfBirth: Yup.date().nullable(),
    placeOfBirth: Yup.string().nullable(),
    gender: Yup.string().nullable(),
    bloodGroupId: Yup.string().nullable(),
    examBoardId: Yup.string().nullable(),
    nationalityId: Yup.string().nullable(),
    motherTongueId: Yup.string().nullable(),
    studentTypeId: Yup.string().nullable(),
    casteId: Yup.string().nullable(),
    categoryId: Yup.string().nullable(),
    religionId: Yup.string().nullable(),
    houseId: Yup.string().nullable(),
    mediumId: Yup.string().nullable(),
    parent: Yup.object().shape({
      fatherName: Yup.string().nullable(),
      fatherMobile: Yup.string().matches(/^\d{10}$/, { message: 'Father Mobile must be exactly 10 digits numeric', excludeEmptyString: true }).nullable(),
      fatherOccupationId: Yup.string().nullable(),
      motherName: Yup.string().nullable(),
      motherMobile: Yup.string().matches(/^\d{10}$/, { message: 'Mother Mobile must be exactly 10 digits numeric', excludeEmptyString: true }).nullable(),
      motherOccupationId: Yup.string().nullable(),
      guardianName: Yup.string().nullable(),
      guardianMobile: Yup.string().matches(/^\d{10}$/, { message: 'Guardian Mobile must be exactly 10 digits numeric', excludeEmptyString: true }).nullable(),
      guardianEmail: Yup.string().email('Invalid email').nullable(),
      guardianRelationId: Yup.string().nullable(),
    }),
    addresses: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().required(),
        addressLine1: Yup.string().required('Address Line 1 is required'),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        pincode: Yup.string().matches(/^\d{6}$/, 'Pincode must be 6 digits').required('Pincode is required'),
      })
    ),
    previousSchool: Yup.object().shape({
      schoolName: Yup.string().required('Previous School Name is required'),
      boardId: Yup.string().nullable(),
      passingYear: Yup.number().nullable(),
      previousClass: Yup.string().nullable(),
      mediumId: Yup.string().nullable(),
      medium: Yup.string().nullable(),
      tcNumber: Yup.string().nullable(),
      tcDate: Yup.date().nullable(),
      result: Yup.string().nullable(),
    }).nullable(),
    isDeclared: Yup.boolean().oneOf([true], 'You must declare the information is correct'),
    studentPhotoUrl: Yup.string().nullable(),
    isPermanentSameAsPresent: Yup.boolean(),
    documents: Yup.array().test('mandatory-docs', 'Missing mandatory documents', function (value) {
      if (!masterDocuments) return true;
      const uploadedDocIds = value?.map((d: any) => d.documentId) || [];
      const missingMandatory = masterDocuments
        .filter((d: any) => d.isMandatory && (d.applicableFor === 'STUDENT' || d.applicableFor === 'BOTH' || !d.applicableFor))
        .filter((d: any) => !uploadedDocIds.includes(d.id));
      
      if (missingMandatory.length > 0) {
        return this.createError({
          message: `Missing mandatory documents: ${missingMandatory.map((d: any) => d.name).join(', ')}`,
          path: 'documents',
        });
      }
      return true;
    }),
  }), [masterDocuments]);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  const fetchSections = useCallback(async (classId: string) => {
    if (!classId) {
      setSections([]);
      return;
    }
    const data = await fetchSectionsApi(classId);
    setSections(data);
  }, [fetchSectionsApi]);

  useEffect(() => {
    if (initialValues?.classId) {
      fetchSections(initialValues.classId);
    }
  }, [initialValues, fetchSections]);

  if (loadingMasters) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const defaultValues = {
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    placeOfBirth: '',
    gender: '',
    bloodGroup: '',
    bloodGroupId: initialValues?.bloodGroupId || (initialValues?.bloodGroupMaster?.id) || (initialValues?.bloodGroup && bloodGroups.find(bg => bg.name === initialValues.bloodGroup)?.id) || '',
    examBoardId: initialValues?.examBoardId || (initialValues?.examBoard?.id) || '',
    aadharNumber: '',
    status: StudentStatus.ACTIVE,
    classId: '',
    sectionId: '',
    academicSessionId: initialValues?.academicSessionId || academicSessions.find((s: any) => s.isActive)?.id || '',
    admissionNumber: '',
    scholarNumber: '',
    admissionDate: new Date().toISOString().split('T')[0],
    mediumId: initialValues?.mediumId || initialValues?.medium?.id || '',
    rte: false,
    bpl: false,
    casteId: '',
    categoryId: '',
    religionId: '',
    houseId: '',
    nationalityId: '',
    motherTongueId: '',
    studentTypeId: '',
    parent: {
      fatherName: initialValues?.parent?.fatherName || '',
      fatherMobile: initialValues?.parent?.fatherMobile || '',
      fatherOccupationId: initialValues?.parent?.fatherOccupationId || '',
      fatherQualification: initialValues?.parent?.fatherQualification || '',
      fatherAnnualIncome: initialValues?.parent?.fatherAnnualIncome || '',
      motherName: initialValues?.parent?.motherName || '',
      motherMobile: initialValues?.parent?.motherMobile || '',
      motherOccupationId: initialValues?.parent?.motherOccupationId || '',
      motherQualification: initialValues?.parent?.motherQualification || '',
      motherAnnualIncome: initialValues?.parent?.motherAnnualIncome || '',
      guardianName: initialValues?.parent?.guardianName || '',
      guardianMobile: initialValues?.parent?.guardianMobile || '',
      guardianRelationId: initialValues?.parent?.guardianRelationId || '',
      guardianAddress: initialValues?.parent?.guardianAddress || '',
      guardianEmail: initialValues?.parent?.guardianEmail || '',
    },
    addresses: initialValues?.addresses || [
      { type: 'PRESENT', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', district: '', country: 'India' },
      { type: 'PERMANENT', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', district: '', country: 'India' }
    ],
    previousSchool: {
      schoolName: '',
      boardId: '',
      passingYear: '',
      previousClass: '',
      mediumId: '',
      medium: '',
      result: '',
      tcNumber: '',
      tcDate: '',
      leavingReasonId: '',
    },
    documents: [],
    remarks: '',
    isDeclared: false,
    studentPhotoUrl: '',
    fatherPhotoUrl: '',
    motherPhotoUrl: '',
    guardianPhotoUrl: '',
    isPermanentSameAsPresent: false,
  };

  const mergedValues = {
    ...defaultValues,
    ...(initialValues || {}),
    // force flat ids and preserve booleans
    bloodGroupId: initialValues?.bloodGroupId || initialValues?.bloodGroup?.id || initialValues?.bloodGroupMaster?.id || '',
    examBoardId: initialValues?.examBoardId || initialValues?.examBoard?.id || '',
    mediumId: initialValues?.mediumId || initialValues?.medium?.id || '',
    casteId: initialValues?.casteId || initialValues?.caste?.id || '',
    categoryId: initialValues?.categoryId || initialValues?.category?.id || '',
    religionId: initialValues?.religionId || initialValues?.religion?.id || '',
    houseId: initialValues?.houseId || initialValues?.house?.id || '',
    nationalityId: initialValues?.nationalityId || initialValues?.nationality?.id || '',
    motherTongueId: initialValues?.motherTongueId || initialValues?.motherTongue?.id || '',
    studentTypeId: initialValues?.studentTypeId || initialValues?.studentType?.id || '',
    isDeclared: initialValues?.isDeclared === true,
    isPermanentSameAsPresent: initialValues?.isPermanentSameAsPresent === true,
    documents: (initialValues?.documents || []).map((d: any) => ({
      ...d,
      documentId: d.documentId || d.id || d.document?.id,
      name: d.name || d.document?.name || '',
      url: d.url || d.filePath || d.document?.url || '',
    })),
    previousSchool: {
      ...defaultValues.previousSchool,
      ...(initialValues?.previousSchool || {}),
      boardId: initialValues?.previousSchool?.boardId || initialValues?.previousSchool?.board?.id || '',
      mediumId: initialValues?.previousSchool?.mediumId || initialValues?.previousSchool?.medium?.id || '',
      leavingReasonId: initialValues?.previousSchool?.leavingReasonId || initialValues?.previousSchool?.leavingReason?.id || '',
    },
  };

  const handleSameAsPresentChange = (e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any, values: any) => {
      const isChecked = e.target.checked;
      setFieldValue('isPermanentSameAsPresent', isChecked);
      if (isChecked) {
          const presentAddr = values.addresses.find((a: any) => a.type === 'PRESENT');
          if (presentAddr) {
              const newAddresses = values.addresses.map((a: any) => 
                  a.type === 'PERMANENT' ? { ...presentAddr, type: 'PERMANENT' } : a
              );
              setFieldValue('addresses', newAddresses);
          }
      }
  };

  const handleAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, 
    index: number, 
    fieldName: string, 
    setFieldValue: any, 
    values: any
  ) => {
    const value = e.target.value;
    const addressType = values.addresses[index].type;
    
    // Update the field being changed
    setFieldValue(`addresses.${index}.${fieldName}`, value);
    
    // If it's PRESENT address and sync is on, update PERMANENT address
    if (addressType === 'PRESENT' && values.isPermanentSameAsPresent) {
        const permIndex = values.addresses.findIndex((a: any) => a.type === 'PERMANENT');
        if (permIndex !== -1) {
            setFieldValue(`addresses.${permIndex}.${fieldName}`, value);
        }
    }
  };

  return (
    <PageContainer>
      <PageHeader title={isEdit ? "Edit Student" : "Create Student"} />
      
      <Formik
        initialValues={mergedValues}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
          onSubmit(values);
        }}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, setFieldValue, isSubmitting, getFieldProps, submitForm, isValid }) => {
             // Render Field Helper
            const renderFormField = (fieldDefinition: any, prefix = '') => {
                const isRelation = fieldDefinition.isRelation;
                const baseName = fieldDefinition.name;
                
                // Map base name to form field name
                let fieldName = baseName;
                if (isRelation && !baseName.endsWith('Id')) fieldName = `${baseName}Id`;
                
                if (baseName === 'bloodGroup' || baseName === 'bloodGroupMaster') fieldName = 'bloodGroupId'; // Special mapping
                if (baseName === 'leavingReason') fieldName = 'leavingReasonId';
                if (baseName === 'examBoard') fieldName = 'examBoardId';
                if (baseName === 'board' && prefix === 'previousSchool') fieldName = 'boardId';

                const fullName = prefix ? `${prefix}.${fieldName}` : fieldName;
                const label = fieldDefinition.label;
                const grid = fieldDefinition.grid;
                
                // Error handling
                // For nested fields like parent.fatherName, touched/errors are nested
                let error = (errors as any)[fullName];
                let isTouched = (touched as any)[fullName];
                
                if (prefix) {
                    const prefixError = (errors as any)[prefix];
                    const prefixTouched = (touched as any)[prefix];
                    if (prefixError) error = prefixError[fieldName];
                    if (prefixTouched) isTouched = prefixTouched[fieldName];
                }

                const hasError = Boolean(isTouched && error);

                // 1. Boolean Fields (Yes/No Select)
                if (fieldDefinition.type === 'boolean') {
                    const val = prefix ? (values as any)[prefix]?.[fieldName] : (values as any)[fullName];
                    return (
                        <Grid item xs={12} md={grid} key={fullName}>
                            <FormControl fullWidth error={hasError}>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                        name={fullName}
                                        value={val === true ? 'true' : val === false ? 'false' : ''}
                                        label={label}
                                        onChange={(e) => setFieldValue(fullName, e.target.value === 'true')}
                                    >
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    );
                }

                // 2. Select Fields (Relations & Enums)
                if (isRelation || baseName === 'gender' || baseName === 'bloodGroup' || baseName === 'bloodGroupId' || baseName === 'bloodGroupMaster' || baseName === 'examBoard' || baseName === 'examBoardId' || baseName === 'studentType' || baseName === 'medium' || baseName === 'board') {
                    let options: any[] = [];
                    const val = prefix ? (values as any)[prefix]?.[fieldName] : (values as any)[fullName];
                    
                    if (baseName === 'gender') options = [{id: 'MALE', name: 'Male'}, {id: 'FEMALE', name: 'Female'}, {id: 'OTHER', name: 'Other'}];
                    else if (baseName === 'bloodGroup' || baseName === 'bloodGroupId' || baseName === 'bloodGroupMaster') options = bloodGroups;
                    else if (baseName === 'examBoard' || baseName === 'examBoardId') options = examBoards;
                    else if (baseName === 'caste') options = castes;
                    else if (baseName === 'category') options = categories;
                    else if (baseName === 'religion') options = religions;
                    else if (baseName === 'house') options = houses;
                    else if (baseName === 'nationality') options = nationalities;
                    else if (baseName === 'motherTongue') options = motherTongues;
                    else if (baseName === 'studentType') options = studentTypes;
                    else if (baseName === 'medium') options = mediums;
                    else if (baseName === 'class') options = classes;
                    else if (baseName === 'section') options = sections;
                    else if (baseName === 'fatherOccupation' || baseName === 'motherOccupation') options = parentServiceTypes;
                    else if (baseName === 'guardianRelation') options = familyRelations;
                    else if (baseName === 'leavingReason') options = leavingReasons;
                    else if (baseName === 'board') options = examBoards;

                    // Special Change Handlers
                    const onChange = (e: any) => {
                        handleChange(e);
                        if (baseName === 'class') fetchSections(e.target.value);
                        if (baseName === 'bloodGroup' || baseName === 'bloodGroupId' || baseName === 'bloodGroupMaster') {
                            const selected = bloodGroups.find(bg => bg.id === e.target.value);
                            if (selected) setFieldValue('bloodGroup', selected.name);
                        }
                    };

                    return (
                        <Grid item xs={12} md={grid} key={fullName}>
                            <FormControl fullWidth error={hasError}>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                    name={fullName}
                                    value={val || ''}
                                    label={label}
                                    onChange={onChange}
                                >
                                    {options?.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                    ))}
                                </Select>
                                {hasError && <FormHelperText>{error as string}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    );
                }

                // 3. Date Fields
                if (fieldDefinition.type === 'date') {
                    return (
                        <Grid item xs={12} md={grid} key={fullName}>
                            <TextField
                                name={fullName}
                                label={label}
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={prefix ? ((values as any)[prefix] || {})[fieldName] || '' : (values as any)[fullName] || ''}
                                onChange={handleChange}
                                error={hasError}
                                helperText={hasError ? (error as string) : ''}
                            />
                        </Grid>
                    );
                }

                // 4. Default Text Fields
                return (
                    <Grid item xs={12} md={grid} key={fullName}>
                        <TextField
                            name={fullName}
                            label={label}
                            type={fieldDefinition.type === 'number' ? 'number' : 'text'}
                            fullWidth
                            value={prefix ? ((values as any)[prefix] || {})[fieldName] || '' : (values as any)[fullName] || ''}
                            onChange={handleChange}
                            error={hasError}
                            helperText={hasError ? (error as string) : ''}
                        />
                    </Grid>
                );
            };

            return (
          <Form>
            <FormObserver isSubmitting={isSubmitting} isValid={isValid} errors={errors} />
            <Card sx={{ p: 3, mb: 3 }}>
              {/* Personal Details Section */}
              <Section title="1. Personal Details" defaultExpanded>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                   <PhotoUpload
                      label="Student Photo *"
                      value={values.studentPhotoUrl}
                      onChange={(url) => setFieldValue('studentPhotoUrl', url)}
                      error={touched.studentPhotoUrl && (errors.studentPhotoUrl as string)}
                   />
                </Box>
                <Grid container spacing={2}>
                    {STUDENT_FIELDS.personal.map(field => renderFormField(field))}
                </Grid>
              </Section>

              {/* Academic Details Section */}
              <Section title="Academic Details">
                <Grid container spacing={2}>
                     <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Academic Session</InputLabel>
                            <Select
                                name="academicSessionId"
                                value={values.academicSessionId}
                                label="Academic Session"
                                disabled // Always disabled/read-only as it defaults to active
                            >
                                {(academicSessions || []).map((s: any) => (
                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {STUDENT_FIELDS.academic.map(field => renderFormField(field))}
                </Grid>
              </Section>

              {/* Parent Details Section */}
              <Section title="Parent & Guardian Details">
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Father's Details</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <PhotoUpload label="Father Photo" value={values.fatherPhotoUrl} onChange={(url) => setFieldValue('fatherPhotoUrl', url)} />
                    </Box>
                    <Grid container spacing={2}>
                        {STUDENT_FIELDS.parent.father.map(field => renderFormField(field, 'parent'))}
                    </Grid>
                </Box>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Mother's Details</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <PhotoUpload label="Mother Photo" value={values.motherPhotoUrl} onChange={(url) => setFieldValue('motherPhotoUrl', url)} />
                    </Box>
                    <Grid container spacing={2}>
                        {STUDENT_FIELDS.parent.mother.map(field => renderFormField(field, 'parent'))}
                    </Grid>
                </Box>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Guardian's Details</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <PhotoUpload label="Guardian Photo" value={values.guardianPhotoUrl} onChange={(url) => setFieldValue('guardianPhotoUrl', url)} />
                    </Box>
                    <Grid container spacing={2}>
                        {STUDENT_FIELDS.parent.guardian.map(field => {
                            if (field.name === 'guardianAddress') return null; // Handle manually below if needed, or let generic handler do it (generic handles text field)
                            return renderFormField(field, 'parent');
                        })}
                        <Grid item xs={12}>
                             <TextField
                                name="parent.guardianAddress"
                                label="Guardian Address"
                                fullWidth
                                multiline
                                rows={2}
                                value={values.parent.guardianAddress}
                                onChange={handleChange}
                             />
                        </Grid>
                    </Grid>
                </Box>
              </Section>

              {/* Address Details Section */}
              <Section title="Address Details">
                <FormControlLabel
                    control={
                        <Checkbox 
                            name="isPermanentSameAsPresent"
                            checked={Boolean(values.isPermanentSameAsPresent)}
                            onChange={(e) => handleSameAsPresentChange(e, setFieldValue, values)}
                        />
                    }
                    label="Permanent Address is same as Present Address"
                    sx={{ mb: 2 }}
                 />
                 <Grid container spacing={4}>
                    {/* Present Address */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Present Address</Typography>
                        <Grid container spacing={2}>
                            {STUDENT_FIELDS.address.map((field) => (
                                <Grid item xs={12} md={field.grid === 12 ? 12 : 6} key={`present-${field.name}`}>
                                    <TextField 
                                        label={field.label}
                                        fullWidth
                                        value={(values.addresses[0] as any)[field.name]}
                                        onChange={(e) => handleAddressChange(e, 0, field.name, setFieldValue, values)}
                                        error={(touched.addresses as any)?.[0]?.[field.name] && Boolean((errors.addresses as any)?.[0]?.[field.name])}
                                        helperText={(touched.addresses as any)?.[0]?.[field.name] && (errors.addresses as any)?.[0]?.[field.name]}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                    
                    {/* Permanent Address */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Permanent Address</Typography>
                        <Grid container spacing={2}>
                            {STUDENT_FIELDS.address.map((field) => (
                                <Grid item xs={12} md={field.grid === 12 ? 12 : 6} key={`permanent-${field.name}`}>
                                    <TextField 
                                        label={field.label}
                                        fullWidth
                                        value={(values.addresses[1] as any)[field.name]}
                                        onChange={(e) => handleAddressChange(e, 1, field.name, setFieldValue, values)}
                                        disabled={values.isPermanentSameAsPresent}
                                        error={(touched.addresses as any)?.[1]?.[field.name] && Boolean((errors.addresses as any)?.[1]?.[field.name])}
                                        helperText={(touched.addresses as any)?.[1]?.[field.name] && (errors.addresses as any)?.[1]?.[field.name]}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                 </Grid>
              </Section>

              {/* Previous School Details */}
              <Section title="Previous School Details">
                 <Grid container spacing={2}>
                    {STUDENT_FIELDS.previousSchool.map(field => renderFormField(field, 'previousSchool'))}
                 </Grid>
              </Section>

              {/* Documents Section */}
              <Section title="Documents Upload">
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell>Document Name</TableCell>
                        <TableCell>Required</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {masterDocuments
                        ?.filter((doc: any) => doc.applicableFor === 'STUDENT' || doc.applicableFor === 'BOTH' || !doc.applicableFor)
                        .map((masterDoc: any) => {
                          const uploadedDoc = values.documents.find((d: any) => d.documentId === masterDoc.id);
                          const isMandatory = masterDoc.isMandatory;

                          return (
                            <TableRow key={masterDoc.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="500">{masterDoc.name}</Typography>
                              </TableCell>
                              <TableCell>
                                {isMandatory ? <Chip label="Mandatory" color="error" size="small" /> : <Chip label="Optional" size="small" />}
                              </TableCell>
                              <TableCell>
                                {uploadedDoc ? (
                                  <Chip label="Uploaded" color="success" size="small" icon={<CloudUploadIcon />} />
                                ) : (
                                  <Chip label="Pending" color="warning" size="small" />
                                )}
                              </TableCell>
                              <TableCell>
                                {uploadedDoc ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="text"
                                      color="primary"
                                      href={getFullUrl(uploadedDoc.url)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {uploadedDoc.name || 'View File'}
                                    </Button>
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => {
                                        const newDocs = values.documents.filter((d: any) => d.documentId !== masterDoc.id);
                                        setFieldValue('documents', newDocs);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Button
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CloudUploadIcon />}
                                  >
                                    Upload
                                    <input
                                      type="file"
                                      hidden
                                      onChange={async (e) => {
                                        if (e.target.files && e.target.files[0]) {
                                          try {
                                            const file = e.target.files[0];
                                            const response = await studentsApi.uploadFile(file);
                                            const newDoc = {
                                              documentId: masterDoc.id,
                                              name: file.name,
                                              url: response.url
                                            };
                                            setFieldValue('documents', [...values.documents, newDoc]);
                                          } catch (err) {
                                            alert('Document upload failed');
                                          }
                                        }
                                      }}
                                    />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {(!masterDocuments || masterDocuments.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No documents configured in system.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {touched.documents && typeof errors.documents === 'string' && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {errors.documents}
                  </Typography>
                )}
              </Section>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isDeclared"
                      checked={Boolean(values.isDeclared)}
                      onChange={handleChange}
                    />
                  }
                  label="I hereby declare that the information provided is true and correct to the best of my knowledge."
                />
                {touched.isDeclared && errors.isDeclared && (
                  <FormHelperText error>{errors.isDeclared as string}</FormHelperText>
                )}
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/scholar-register/students')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={24} /> : (isEdit ? 'Update Student' : 'Create Student')}
                </Button>
              </Box>
            </Card>
          </Form>
        )}}
      </Formik>
    </PageContainer>
  );
};

export default StudentForm;
